import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AddAnotherAccountPermanentPromotionBox from 'src/components/AddAnotherAccountPromotionBox/AddAnotherAccountPromotionBox'
import PromotionBox, {
  CtaDesigns,
} from 'src/components/HomePromotionsStack/PromotionBox/PromotionBox'
import RegisterAccountOnboardingPromotionBox from 'src/components/HomePromotionsStack/RegisterAccountOnboardingPromotionBox/RegisterAccountOnboardingPromotionBox'
import IntegrationsVisibilityPromotionBox from 'src/components/IntegrationsVisibilityPromotionBox/IntegrationsVisibilityPromotionBox'
import RegisterAccountPermanentPromotionBox from 'src/components/RegisterAccountPermanentPromotionBox/RegisterAccountPermanentPromotionBox'
import RequestVirtualCardPromotionBox from 'src/components/RequestVirtualCardPromotionBox/RequestVirtualCardPromotionBox'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import { CookieNames, CookieValuePaths } from 'src/cookies'
import { ImageNames } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useCookieState } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { invitePeopleUrl, profileNotificationsUrl } from 'src/navigation/urls'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { css } from 'styled-components'

interface HomePromotionsStackProps {
  hasVirtualCards: boolean
  relatedToCreditCards: boolean
  isRightSidePromos?: boolean
  hasOrgOwnerPermissions?: boolean
  hasOrgAdminPermissions?: boolean
  activeCreditCards: number
  canRegisterAccount: boolean | undefined
  hasVirtualCardFeatures?: boolean
}

const HomePromotionsStack: React.FC<HomePromotionsStackProps> = (
  props,
): React.ReactElement => {
  const {
    hasVirtualCards,
    relatedToCreditCards,
    isRightSidePromos,
    hasOrgOwnerPermissions,
    hasOrgAdminPermissions,
    activeCreditCards,
    canRegisterAccount,
    hasVirtualCardFeatures,
  } = props || {}

  const { analytics } = useAnalyticsContext()

  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)
  const inviteTeam = useColorOrGrayscaleImage(ImageNames.BuildingsPeople)
  const txn = useColorOrGrayscaleImage(ImageNames.Transaction)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.VirtualCardsActiveOrPendingRecipientMeCountStatistics,
    ])
  }, [dispatchStatistics])

  // highlight "Request Virtual Card" promo box if user is not org owner && vcns == 0 && rcns == 0
  const showRequestCardTooltip =
    !hasOrgOwnerPermissions && !hasVirtualCards && !relatedToCreditCards

  const {
    cookieValue: isIntegrationsPromoOpen,
    updateCookieValue: setIsIntegrationsPromoOpen,
  } = useCookieState(
    CookieNames.Home,
    CookieValuePaths.isIntegrationsPromoOpen,
    true,
  )

  const {
    cookieValue: isRegisterAccountPromoOpen,
    updateCookieValue: setIsRegisterAccountPromoOpen,
  } = useCookieState(
    CookieNames.Home,
    CookieValuePaths.isRegisterAccountPromoOpen,
    true,
  )

  const {
    cookieValue: isAddAnotherAccountHomePromoOpen,
    updateCookieValue: setIsAddAnotherAccountHomePromoOpen,
  } = useCookieState(
    CookieNames.Home,
    CookieValuePaths.isAddAnotherAccountHomePromoOpen,
    true,
  )

  const getLeftSidePromotionOrder = (): React.ReactElement => {
    return (
      <>
        {statisticsLoaded.virtualCardsActiveOrPendingRecipientMeCountStatisticsLoaded &&
          canRegisterAccount && (
            <RegisterAccountOnboardingPromotionBox
              ctaDesign={CtaDesigns.button}
              isHome
            />
          )}
        {hasVirtualCardFeatures && (
          <RequestVirtualCardPromotionBox
            ctaProps={{
              ctaDesign: CtaDesigns.button,
            }}
            isHighlighted={showRequestCardTooltip}
          />
        )}
      </>
    )
  }

  const getRightSidePromotionOrder = (): React.ReactElement => {
    return (
      <>
        {isIntegrationsPromoOpen && !hasOrgOwnerPermissions && (
          <IntegrationsVisibilityPromotionBox
            ctaDesign={CtaDesigns.hoverableLink}
            dismissPromo={(): void => {
              setIsIntegrationsPromoOpen(false)
            }}
          />
        )}
        {(hasVirtualCards || relatedToCreditCards) && (
          <>
            {activeCreditCards === 0 ? (
              <>
                {isRegisterAccountPromoOpen && canRegisterAccount && (
                  <RegisterAccountPermanentPromotionBox
                    dismissPromo={(): void => {
                      setIsRegisterAccountPromoOpen(false)
                    }}
                  />
                )}
              </>
            ) : (
              <>
                {isAddAnotherAccountHomePromoOpen &&
                  relatedToCreditCards &&
                  canRegisterAccount && (
                    <AddAnotherAccountPermanentPromotionBox
                      dismissPromo={(): void => {
                        setIsAddAnotherAccountHomePromoOpen(false)
                      }}
                    />
                  )}
              </>
            )}
            {hasVirtualCardFeatures && (
              <RequestVirtualCardPromotionBox
                ctaProps={{
                  ctaDesign: CtaDesigns.hoverableLink,
                  ctaContent: (
                    <>
                      <Trns source={'Request Virtual Card'} />
                      <SvgArrowForward
                        css={css`
                          transform: scale(calc(14 / 24));
                        `}
                      />
                    </>
                  ),
                }}
                isHighlighted={showRequestCardTooltip}
              />
            )}
          </>
        )}
        {/* show if user is admin or if they are owner and onboarding checklist not released */}
        {hasOrgAdminPermissions && (
          <PromotionBox
            imageSrc={inviteTeam}
            title={<Trns source={'Invite your team'} />}
            subtitle={
              <Trns
                source={
                  'Bring your team on board for better visibility and delegation.'
                }
              />
            }
            id={'invitePeople'}
            ctaProps={{
              ctaDesign: CtaDesigns.hoverableLink,
              ctaContent: (
                <>
                  <Trns source={'Invite team'} />
                  <SvgArrowForward
                    css={css`
                      transform: scale(calc(14 / 24));
                    `}
                  />
                </>
              ),
              ctaTo: invitePeopleUrl(),
              ctaAriaLabelSource: 'Invite team',
              onCtaClick: (): void => {
                analytics?.track(
                  'home invite to org promo - open invite form - text link press',
                  {
                    screen: '/home',
                    interaction: 'text link press',
                    intent: 'open invite form',
                  },
                )
              },
            }}
          />
        )}
        <PromotionBox
          imageSrc={txn}
          title={<Trns source={'Manage notifications'} />}
          subtitle={
            <Trns
              source={
                'Visit Notification settings to manage how we notify you.'
              }
            />
          }
          ctaProps={{
            ctaDesign: CtaDesigns.hoverableLink,
            ctaContent: (
              <>
                <Trns source={'View Notification Settings'} />
                <SvgArrowForward
                  css={css`
                    transform: scale(calc(14 / 24));
                  `}
                />
              </>
            ),
            ctaTo: profileNotificationsUrl(),
            ctaAriaLabelSource: 'View Notification Settings',
          }}
        />
      </>
    )
  }

  return isRightSidePromos
    ? getRightSidePromotionOrder()
    : getLeftSidePromotionOrder()
}

export default HomePromotionsStack
